import React, { useMemo } from 'react';
import { Box, Preloader } from '@common-fe/common-fe';
import styled from 'styled-components';

import { ContentWrapper, ModalWrapper } from '@/components/wrappers';
import { AllowedPaymentMethodConfig } from '@/modules/employer/types';
import { PaymentMethodType } from '@/modules/ReimburseMe/types/payment.types';

import { usePaymentMethodsStore } from '../usePaymentMethods.store';

import { usePaymentMethodsCards } from './hooks/usePaymentMethodsCards';
import AddCard from './AddCard';
import ChoosePaymentMethodFooter from './ChoosePaymentMethodFooter';

const Wrapper = styled.span`
  > div:not(:first-child) {
    margin-top: ${({ theme }) => theme.spacings.spacing8};
  }
`;

interface Props {
  onClose: () => void;
  chooseDeposit?: () => void;
  chooseDebitCard?: () => void;
  choosePayPal?: () => void;
  chooseVenmo?: () => void;
  chooseCheck?: () => void;
  editPaymentMethod?: () => void;
  onlyAddingMode?: boolean;
  allowedPaymentMethodConfigs?: AllowedPaymentMethodConfig[];
  isUnwrapped?: boolean;
  isLoading?: boolean;
}

const ChoosePaymentMethod: React.FC<Props> = ({
  onClose,
  chooseDeposit,
  chooseDebitCard,
  choosePayPal,
  chooseVenmo,
  chooseCheck,
  editPaymentMethod,
  onlyAddingMode,
  allowedPaymentMethodConfigs,
  isUnwrapped,
  isLoading,
}) => {
  const paymentMethods = usePaymentMethodsStore((state) => state.methods);
  const allowedPaymentMethods = useMemo(() => {
    if (!allowedPaymentMethodConfigs?.length) return [];

    return allowedPaymentMethodConfigs
      .map((item) => item.paymentMethodType) as PaymentMethodType[];
  },
  [allowedPaymentMethodConfigs]);
  const cards = usePaymentMethodsCards({
    onClose,
    chooseDeposit,
    chooseDebitCard,
    choosePayPal,
    chooseVenmo,
    chooseCheck,
    methods: paymentMethods,
    allowedPaymentMethods,
  });

  const availableCardsToAdd = useMemo(() => {
    if (onlyAddingMode) {
      return cards.filter((card) => card.paymentType !== PaymentMethodType.CHECK);
    }

    return cards;
  }, [cards, onlyAddingMode]);

  const renderedCards = useMemo(() => (
    <Wrapper>
      {availableCardsToAdd.map((card) => (
        <AddCard
          key={card.paymentType}
          title={card.title}
          paymentType={card.paymentType}
          description={card.description}
          add={card?.add}
          addTitle={card.addTitle}
          paymentMethods={card.paymentMethods}
          editPaymentMethod={editPaymentMethod}
          onlyAddingMode={onlyAddingMode}
          hasPaymentFreeTimeLabel={false}
          hideNonDebitIcons
          allowedPaymentMethodConfigs={allowedPaymentMethodConfigs}
        />
      ))}
    </Wrapper>
  ),[
    availableCardsToAdd,
    editPaymentMethod,
    onlyAddingMode,
    allowedPaymentMethodConfigs,
  ]);

  if (isUnwrapped) {
    return (
      <Box>
        {isLoading ? (<Box pad="spacing12"><Preloader /></Box>) : renderedCards}
      </Box>
    );
  }

  return (
    <ModalWrapper
      visible
      title="Choose a Payment Method"
      onSetVisible={onClose}
      testId="choose_payment_method"
    >
      <ContentWrapper>
        {renderedCards}
      </ContentWrapper>

      {!onlyAddingMode && <ChoosePaymentMethodFooter onClose={onClose} />}
    </ModalWrapper>
  );
};

export default ChoosePaymentMethod;
